import type { CustomIcon } from '../../interfaces/CustomIcon';

export interface TableColumnModel {
  id: number;
  header?: string;
  subTitle?: string;
  key: string;
  type: TableColumnType;
  icon?: CustomIcon;
  sortable: boolean;
  frozen?: boolean;
  identifier?: string;
  value?: string | number;
  exportFormNames?: string;

  /*
   * The fields below are what `Table.vue` needs to draw a cell of `type` without the caller writing
   * a `#bodyContent` slot for it.
   *
   * **No store sets any of them yet, and every caller still writes that slot.** They are the
   * surface adoption moves onto, one list at a time, and until then `Table.vue`'s cell rendering is
   * slot *fallback* that nothing reaches. Each field is a divergence that is hardcoded into those
   * nine templates today — the avatar's row key, the date's format, the archive's per-row icon —
   * and the store that declares the column is where each belongs, because the store is what knows
   * the row shape.
   */

  /** `DATE` only. Defaults to `DATE_FORMAT_2`, which is what every list but the group list uses. */
  dateFormat?: string;
  /**
   * `TEXT_WITH_ICON` only: the row field holding this cell's icon, where the icon varies row by row
   * rather than being the column's. `Archive.vue` reads `data.icon`; the rest use `col.icon`.
   */
  iconKey?: string;
  /** `USER` only: the row field holding the avatar image. `creatorAvatar` on a case, `avatar` on a user. */
  avatarKey?: string;
  /** `USER` only: the row field holding the address shown under the name. The user lists show one. */
  emailKey?: string;
  /** `USER` only. 28px in every list but the user list, which draws a 44px row. */
  avatarSize?: number;
  /**
   * What to draw where the row has no value. "System Generated" on a case's creator, which is a
   * real statement about the case and not a placeholder.
   */
  emptyText?: string;
}

// eslint-disable-next-line no-shadow
export enum TableColumnType {
  TEXT,
  USER,
  TEXT_WITH_ICON,
  BUTTON_WITH_ICON,
  SQUARE_BUTTON_WITH_ICON,
  SELECT,
  PIPELINE,
  /**
   * Appended rather than slotted in beside `TEXT`: these are numeric enum members, so inserting one
   * renumbers every member after it.
   *
   * A date is a `TEXT` column that each caller reformats in its own slot, by matching `col.key`
   * against the string `'createdAt'`. Saying so on the column is what will let the table draw it —
   * no store declares a `DATE` column yet, so this is unreached until a list adopts it.
   */
  DATE
}
